import deepEqual from 'deep-equal';
import merge from 'lodash/merge';
import { loadQuery, type PreloadedQuery } from 'react-relay';
import { type IEnvironment } from 'relay-runtime';

import { PRELOAD_SEARCH_PAGE_STATE } from '../../../common/constants/relay/preload-state';
import type { SearchPageState } from '../../../common/constants/relay/types';
import type { DeepPartial } from '../../../common/types';
import { makeAbortable } from '../../../common/utils/abortable-promise';
import { makeDisposable } from '../../../common/utils/disposable-promise';
import { disposableCallback, disposableValue } from '../../../common/utils/disposable-value';
import { serveSearchPagePreload } from '../../../common/utils/experiments';
import { fetchLeapQuery, type FetchLeapQueryProps } from '../../../common/utils/leap-query';
import { disablePreloading, enablePreloading } from '../../../common/utils/preload';
import { isSearchable } from '../../../common/utils/relay/is-searchable';
import RightPanelQuery, {
	type rightPanelQuery,
	type rightPanelQuery$variables,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../right-panel/__generated__/rightPanelQuery.graphql';
import SearchResultsQuery, {
	type searchResultsQuery,
	type searchResultsQuery$variables,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../search-results/__generated__/searchResultsQuery.graphql';

import { getRelayFetchOptions } from './get-relay-fetch-options';
import { getVariables } from './get-variables';
import { PRELOAD_VALUES } from './preload-values';
import { getSessionIds, SEARCH_SESSION_ID, setSearchSessionId } from './session';

/**
 * Disposes a preloaded query, ensuring that any ongoing network requests are completed before disposal.
 */
export function disposePreloadedQuery(
	value: PreloadedQuery<searchResultsQuery> | PreloadedQuery<rightPanelQuery> | null | undefined,
	onDispose?: () => void,
) {
	const source = value?.source;
	if (!source) {
		value?.dispose();
		onDispose?.();
		return;
	}
	source.toPromise()?.finally(() => {
		value?.dispose();
		onDispose?.();
	});
}

/**
 * Disposable cancellable promise that resolves when both Relay queries complete.
 * This ensures that we don't disable preloading if a new search is initiated.
 * @param relaySearchResults
 * @param relayRightPanel
 * @returns
 */
export function relayQueriesToDisposablePromise(
	relaySearchResults: PreloadedQuery<searchResultsQuery> | null | undefined,
	relayRightPanel: PreloadedQuery<rightPanelQuery> | null | undefined,
) {
	return makeDisposable(
		makeAbortable(
			Promise.all([relaySearchResults?.source?.toPromise(), relayRightPanel?.source?.toPromise()]),
		),
	);
}

/**
 * Returns a function that preloads both the search results and right panel queries.
 */
export const preloadQueryLoader =
	(environment: IEnvironment, logError?: (error: Error) => void) =>
	(
		searchResultsVars: searchResultsQuery$variables,
		rightPanelVars: rightPanelQuery$variables,
		state: SearchPageState,
		leapQueryProps: FetchLeapQueryProps,
	): boolean => {
		const [preloadRelaySearchResults] = PRELOAD_VALUES.value() || [];
		const preloadSearchResults = preloadRelaySearchResults?.value();
		const shouldFetch = !deepEqual(preloadSearchResults?.variables, searchResultsVars);
		if (shouldFetch) {
			const relayFetchOptions = getRelayFetchOptions(state.bootstrap);
			// start preloading state
			enablePreloading();
			// Preload both queries
			const relaySearchResults = loadQuery<searchResultsQuery, Record<string, unknown>>(
				environment,
				SearchResultsQuery,
				searchResultsVars,
				relayFetchOptions,
			);
			const relayRightPanel = loadQuery<rightPanelQuery, Record<string, unknown>>(
				environment,
				RightPanelQuery,
				rightPanelVars,
				relayFetchOptions,
			);
			// Fetch leap query
			fetchLeapQuery(leapQueryProps, logError);
			// Wait for both queries to resolve before marking preloading as complete
			const promise = relayQueriesToDisposablePromise(relaySearchResults, relayRightPanel);
			// clear preloading state when done
			promise.finally(disablePreloading);
			// Store the preloaded queries and state
			PRELOAD_VALUES.update([
				disposableValue(relaySearchResults, disposePreloadedQuery),
				disposableValue(relayRightPanel, disposePreloadedQuery),
				state,
				leapQueryProps,
				promise,
				// used for cleanup purposes
				disposableCallback(disablePreloading),
			]);
			// start preloading state
			enablePreloading();
		}
		return shouldFetch;
	};

/**
 * Gets the search and right panel variables along with an updated state that includes session IDs.
 */
const getVariablesWithSession = (state: SearchPageState) => {
	const { bootstrap, search, selectedInputFilters } = state;
	if (!isSearchable(search.availableProducts, search.selectedProducts[0], search.searchQuery)) {
		return;
	}
	const [searchResultsVars, rightPanelVars] = getVariables(
		bootstrap,
		search,
		selectedInputFilters,
		serveSearchPagePreload(bootstrap.primaryProduct) ? SEARCH_SESSION_ID.value() : undefined,
	);
	// Hack to ensure sessionId/searchSessionId doesn't break the cache
	const [sessionId, searchSessionId] = getSessionIds(searchResultsVars);
	const searchResultsVarsWithSession = { ...searchResultsVars, sessionId, searchSessionId };
	const rightPanelVarsWithSession = { ...rightPanelVars, sessionId, searchSessionId };
	const nextState = merge({}, state, {
		bootstrap: { searchSessionId: searchResultsVars.searchSessionId },
	});
	return [searchResultsVarsWithSession, rightPanelVarsWithSession, nextState] as const;
};

/**
 * Gets the search and right panel variables along with an updated state that includes session IDs.
 * Also sets a unique search session ID if not already set.
 * Runs the provided loader function to fetch the queries.
 */
export const runQueryLoader = (
	state: SearchPageState,
	stateOverride: DeepPartial<SearchPageState> = {},
	loaderFn: (
		searchResultsVars: searchResultsQuery$variables,
		rightPanelVars: rightPanelQuery$variables,
		state: SearchPageState,
		leapQueryProps: FetchLeapQueryProps,
	) => void,
	isAdminHubAIEnabled: boolean,
	isRovoLLMEnabled: boolean,
) => {
	const stateWithOverride = merge({}, state, stateOverride);
	const search = stateWithOverride.search;
	if (!isSearchable(search.availableProducts, search.selectedProducts[0], search.searchQuery)) {
		return;
	}
	const vars = getVariablesWithSession(stateWithOverride);
	if (!vars) {
		return;
	}
	const [searchResultsVars, rightPanelVars, nextState] = vars;
	const searchQuery = searchResultsVars.query;
	const { primaryProduct, isRovoEnabled } = nextState.bootstrap;
	loaderFn(searchResultsVars, rightPanelVars, nextState, {
		searchQuery,
		primaryProduct,
		isRovoEnabled,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	});
};

/**
 * Preloads the search query and right panel data based on the provided state override.
 */
export const preloadSearchPageQuery = (
	stateOverride: DeepPartial<SearchPageState> = {},
	logError?: (error: Error) => void,
): void => {
	const preloadState = PRELOAD_SEARCH_PAGE_STATE.value();
	// Only preload if we have a valid primary product and the experiment is enabled
	if (
		!preloadState ||
		!preloadState.bootstrap.primaryProduct ||
		!serveSearchPagePreload(preloadState.bootstrap.primaryProduct, true)
	) {
		return;
	}
	setSearchSessionId(); // set session id if not already set
	const { environment, isAdminHubAIEnabled, isRovoLLMEnabled, ...state } = preloadState;
	runQueryLoader(
		state,
		stateOverride,
		preloadQueryLoader(environment, logError),
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	);
};
